// VoiceManagementWithHistory.jsx - Enhanced VoiceManagement with TTS History Integration
import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  Stack,
  Grid,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  Badge,
  CircularProgress
} from '@mui/material';
import {
  RecordVoiceOver as VoiceIcon,
  VolumeUp as TTSIcon,
  Analytics as AnalyticsIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Upgrade as UpgradeIcon,
  MicOff as MicOffIcon,
  Language as LanguageIcon
} from '@mui/icons-material';

// Import the TTSHistoryPanel component
import TTSHistoryPanel from './TTSHistoryPanel';

const VoiceManagementWithHistory = ({ 
  businessId, 
  businessName, 
  accountType = 'free',
  voiceLibrary = null,
  quotaUsage = null,
  onVoiceUpdate,
  onRefresh // Callback to refresh data from parent
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [voiceToDelete, setVoiceToDelete] = useState(null);

  const token = useSelector((state) => state.auth?.token);

  // Quota limits by account type
  const quotaLimits = {
    free: {
      voiceSamples: 1,
      ttsCharacters: 500,
      description: 'Free Plan'
    },
    premium: {
      voiceSamples: 5,
      ttsCharacters: 5000,
      description: 'Premium Plan'
    },
    enterprise: {
      voiceSamples: -1, // Unlimited
      ttsCharacters: 20000,
      description: 'Enterprise Plan'
    }
  };

  const currentLimits = quotaLimits[accountType] || quotaLimits.free;

  // Get current month key for quota calculations
  const currentMonthKey = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth() + 1}`;
  }, []);

  // Extract voice data from props
  const voices = voiceLibrary?.voices || [];
  const totalVoices = voiceLibrary?.totalVoices || 0;
  const lastUpdated = voiceLibrary?.lastUpdated;

  // Calculate current usage from quota data
  const currentUsage = useMemo(() => {
    if (!quotaUsage) {
      return {
        samplesCreated: voices.length,
        ttsCharactersUsed: 0,
        monthlyTtsUsage: 0,
        lifetimeTtsUsage: 0,
        totalCharacters: 0
      };
    }

    // Voice cloning quota (voice samples)
    const voiceQuota = quotaUsage.voice_cloning || {};
    
    // TTS generation quota (characters used)
    const ttsQuota = quotaUsage.voice_tts_generation || {};
    
    return {
      samplesCreated: voices.length,
      // TTS character usage from voice_tts_generation
      ttsCharactersUsed: ttsQuota.lifetime || 0,
      monthlyTtsUsage: ttsQuota.monthly?.[currentMonthKey] || ttsQuota.monthly?.[`${currentMonthKey}_characters`] || 0,
      lifetimeTtsUsage: ttsQuota.lifetime || ttsQuota.totalCharacters || 0,
      totalCharacters: ttsQuota.totalCharacters || ttsQuota.lifetime || 0,
      // Additional TTS metadata
      lastTTSUsed: ttsQuota.lastUsed,
      lastEmotionalInstructions: ttsQuota.lastEmotionalInstructions,
      byVoiceUsage: ttsQuota.byVoice || {}
    };
  }, [quotaUsage, voices.length, currentMonthKey]);

  // Calculate usage percentages
  const usagePercentages = useMemo(() => {
    const samplesPercent = currentLimits.voiceSamples === -1 ? 0 : 
      Math.round((currentUsage.samplesCreated / currentLimits.voiceSamples) * 100);
    
    const ttsPercent = Math.round((currentUsage.monthlyTtsUsage / currentLimits.ttsCharacters) * 100);

    return {
      samples: Math.min(samplesPercent, 100),
      tts: Math.min(ttsPercent, 100)
    };
  }, [currentUsage, currentLimits]);

  // Mock analytics data based on available information
  const analytics = useMemo(() => {
    const activeVoices = voices.filter(v => v.status === 'active');
    const processingVoices = voices.filter(v => v.status === 'processing');
    const failedVoices = voices.filter(v => v.status === 'failed');

    return {
      totalVoices: voices.length,
      activeVoices: activeVoices.length,
      processingVoices: processingVoices.length,
      failedVoices: failedVoices.length,
      charactersUsed: currentUsage.totalCharacters,
      averageVoiceQuality: voices.length > 0 ? 
        Math.round(voices.reduce((sum, v) => sum + (v.metadata?.qualityScore || 0.8), 0) / voices.length * 100) : 0
    };
  }, [voices, currentUsage]);

  const handleDeleteVoice = async (voiceId) => {
    try {
      // TODO: Implement voice deletion API call when backend is ready
      console.log('Deleting voice:', voiceId);
      
      // For now, just close the dialog and trigger refresh
      setDeleteDialogOpen(false);
      setVoiceToDelete(null);
      
      // Trigger refresh from parent component
      onRefresh && onRefresh();
      onVoiceUpdate && onVoiceUpdate();
      
    } catch (err) {
      console.error('Error deleting voice:', err);
      // TODO: Show error message to user
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckIcon color="success" />;
      case 'processing': return <ScheduleIcon color="warning" />;
      case 'failed': return <ErrorIcon color="error" />;
      default: return <WarningIcon color="disabled" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Tab panels
  const VoicesPanel = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Voice Library</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Last updated: {formatDate(lastUpdated)}
            </Typography>
          )}
          <Button
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            size="small"
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {voices.length === 0 ? (
        <Alert severity="info">
          <Typography variant="body2">
            No voice clones created yet. Create your first voice sample to get started with AI speech generation.
          </Typography>
        </Alert>
      ) : (
        <Stack spacing={2}>
          {voices.map((voice) => (
            <Card key={voice._id || voice.voiceId} variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <VoiceIcon color="primary" />
                    <Box>
                      <Typography variant="h6">{voice.voiceName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {voice.voiceDescription || 'No description provided'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getStatusIcon(voice.status)}
                    <Chip 
                      label={voice.status || 'unknown'} 
                      size="small" 
                      color={getStatusColor(voice.status)}
                      variant="outlined"
                    />
                  </Box>
                </Box>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Created</Typography>
                    <Typography variant="body2">{formatDate(voice.createdAt)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Provider</Typography>
                    <Typography variant="body2">{voice.provider || 'ElevenLabs'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Voice ID</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {voice.voiceId || voice._id || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Event Type</Typography>
                    <Typography variant="body2">{voice.eventType || 'BusinessVoiceSample'}</Typography>
                  </Grid>
                </Grid>

                {/* Metadata Section */}
                {voice.metadata && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Technical Details:
                    </Typography>
                    <Grid container spacing={2}>
                      {voice.metadata.fileSize && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">File Size</Typography>
                          <Typography variant="body2">{formatFileSize(voice.metadata.fileSize)}</Typography>
                        </Grid>
                      )}
                      {voice.metadata.originalFileName && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Original File</Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            {voice.metadata.originalFileName.substring(0, 20)}...
                          </Typography>
                        </Grid>
                      )}
                      {voice.metadata.processingTime && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Processing Time</Typography>
                          <Typography variant="body2">{Math.round(voice.metadata.processingTime / 1000)}s</Typography>
                        </Grid>
                      )}
                      {voice.metadata.providerMetadata?.status && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Provider Status</Typography>
                          <Typography variant="body2">{voice.metadata.providerMetadata.status}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <Chip 
                      label="English" 
                      size="small" 
                      variant="outlined"
                      icon={<LanguageIcon />}
                    />
                    {voice.metadata?.providerMetadata?.category && (
                      <Chip 
                        label={voice.metadata.providerMetadata.category} 
                        size="small" 
                        variant="outlined" 
                      />
                    )}
                    {voice.status === 'active' && (
                      <Chip 
                        label="Ready for TTS" 
                        size="small" 
                        color="success"
                        variant="outlined" 
                      />
                    )}
                  </Stack>
                  
                  <Box>
                    {voice.status === 'failed' && (
                      <Button 
                        size="small" 
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => {
                          setVoiceToDelete(voice);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        Remove Failed
                      </Button>
                    )}
                    {voice.status === 'processing' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">
                          Processing...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );

  const QuotaPanel = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Quota Usage</Typography>
      
      {/* Account Type Info */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">
            <strong>{currentLimits.description}</strong> • 
            {currentLimits.voiceSamples === -1 ? ' Unlimited voices' : ` ${currentLimits.voiceSamples} voice samples`} • 
            {currentLimits.ttsCharacters.toLocaleString()} TTS characters per month
          </Typography>
          {accountType === 'free' && (
            <Button size="small" startIcon={<UpgradeIcon />} variant="outlined">
              Upgrade
            </Button>
          )}
        </Box>
      </Alert>

      <Grid container spacing={3}>
        {/* Voice Samples Quota */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <VoiceIcon color="primary" />
                <Typography variant="h6">Voice Samples</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Current Usage</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {currentUsage.samplesCreated}/{currentLimits.voiceSamples === -1 ? '∞' : currentLimits.voiceSamples}
                </Typography>
              </Box>
              
              {currentLimits.voiceSamples !== -1 && (
                <LinearProgress
                  variant="determinate"
                  value={usagePercentages.samples}
                  color={usagePercentages.samples >= 100 ? 'error' : usagePercentages.samples > 80 ? 'warning' : 'primary'}
                  sx={{ mb: 1 }}
                />
              )}
              
              <Typography variant="caption" color="text.secondary">
                {currentLimits.voiceSamples === -1 
                  ? 'Unlimited voice samples with your plan'
                  : `${Math.max(0, currentLimits.voiceSamples - currentUsage.samplesCreated)} samples remaining`
                }
              </Typography>

              {/* Voice Status Breakdown */}
              <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Voice Status Breakdown:
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip 
                    label={`${analytics.activeVoices} Active`} 
                    size="small" 
                    color="success" 
                    variant="outlined"
                  />
                  {analytics.processingVoices > 0 && (
                    <Chip 
                      label={`${analytics.processingVoices} Processing`} 
                      size="small" 
                      color="warning" 
                      variant="outlined"
                    />
                  )}
                  {analytics.failedVoices > 0 && (
                    <Chip 
                      label={`${analytics.failedVoices} Failed`} 
                      size="small" 
                      color="error" 
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* TTS Characters Quota */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <TTSIcon color="primary" />
                <Typography variant="h6">TTS Characters</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">This Month</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {currentUsage.monthlyTtsUsage.toLocaleString()}/{currentLimits.ttsCharacters.toLocaleString()}
                </Typography>
              </Box>
              
              <LinearProgress
                variant="determinate"
                value={usagePercentages.tts}
                color={usagePercentages.tts >= 100 ? 'error' : usagePercentages.tts > 80 ? 'warning' : 'primary'}
                sx={{ mb: 1 }}
              />
              
              <Typography variant="caption" color="text.secondary">
                {Math.max(0, currentLimits.ttsCharacters - currentUsage.monthlyTtsUsage).toLocaleString()} characters remaining this month
              </Typography>

              {/* Lifetime Usage */}
              <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Lifetime Usage:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {currentUsage.totalCharacters.toLocaleString()} characters total
                </Typography>
                {currentUsage.lastTTSUsed && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Last used: {formatDate(currentUsage.lastTTSUsed)}
                  </Typography>
                )}
              </Box>

              {/* Per-Voice Usage Breakdown */}
              {currentUsage.byVoiceUsage && Object.keys(currentUsage.byVoiceUsage).length > 0 && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Usage by Voice (This Month):
                  </Typography>
                  {Object.entries(currentUsage.byVoiceUsage).map(([voiceId, usage]) => {
                    const voice = voices.find(v => v.voiceId === voiceId);
                    const monthlyUsage = typeof usage === 'object' ? usage[currentMonthKey] : usage;
                    
                    return (
                      <Box key={voiceId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption">
                          {voice?.voiceName || `Voice ${voiceId.substring(0, 8)}...`}
                        </Typography>
                        <Typography variant="caption" fontWeight="medium">
                          {monthlyUsage || 0} chars
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Usage Tips */}
      <Alert severity="success" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Optimization Tips:</strong> Reuse voice samples for multiple TTS generations. 
          Use shorter texts for testing. Character quota resets monthly on the 1st. 
          Failed voice samples don't count against your quota.
        </Typography>
      </Alert>
    </Box>
  );

  // *** UPDATED: History Panel now uses TTSHistoryPanel ***
  const HistoryPanel = () => (
    <TTSHistoryPanel
      businessId={businessId}
      businessName={businessName}
      accountType={accountType}
    />
  );

  const AnalyticsPanel = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Voice Analytics</Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {analytics.totalVoices}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Voices
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {analytics.activeVoices}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Voices
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="secondary">
                {analytics.charactersUsed.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Characters Used
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">
                {analytics.averageVoiceQuality}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg. Quality Score
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Voice Performance Breakdown */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Voice Performance</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>Processing Status</Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Successful Voices</Typography>
                    <Chip 
                      label={`${analytics.activeVoices}/${analytics.totalVoices}`} 
                      size="small" 
                      color="success"
                    />
                  </Box>
                  {analytics.processingVoices > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Currently Processing</Typography>
                      <Chip 
                        label={analytics.processingVoices} 
                        size="small" 
                        color="warning"
                      />
                    </Box>
                  )}
                  {analytics.failedVoices > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Failed Voices</Typography>
                      <Chip 
                        label={analytics.failedVoices} 
                        size="small" 
                        color="error"
                      />
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>Usage Metrics</Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Monthly TTS Usage</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {usagePercentages.tts}%
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Voice Samples Used</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {currentLimits.voiceSamples === -1 ? '0%' : `${usagePercentages.samples}%`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Account Type</Typography>
                    <Chip 
                      label={currentLimits.description} 
                      size="small" 
                      color={accountType === 'free' ? 'default' : 'primary'}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {accountType === 'free' && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            Upgrade to Premium or Enterprise for detailed analytics, usage trends, 
            voice performance metrics, and TTS generation history.
          </Typography>
        </Alert>
      )}
    </Box>
  );

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VoiceIcon fontSize="small" />
                Voices
                {voices.length > 0 && (
                  <Badge badgeContent={voices.length} color="primary" />
                )}
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AnalyticsIcon fontSize="small" />
                Quota
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon fontSize="small" />
                TTS History
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon fontSize="small" />
                Analytics
              </Box>
            } 
          />
        </Tabs>
      </Box>

      {activeTab === 0 && <VoicesPanel />}
      {activeTab === 1 && <QuotaPanel />}
      {activeTab === 2 && <HistoryPanel />}
      {activeTab === 3 && <AnalyticsPanel />}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Voice Sample</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the voice "{voiceToDelete?.voiceName}"? 
            This action cannot be undone and will free up space in your voice quota.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => handleDeleteVoice(voiceToDelete?.voiceId || voiceToDelete?._id)}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VoiceManagementWithHistory;